import { useState } from "react";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { Capacitor } from "@capacitor/core";
import { SmallLoader } from "./SmallLoader";

type Variant = "black" | "white" | "gray" | "outlined" | "danger";
type Shape = "rectangle" | "circle";
type Size = "extrasmall" | "small" | "mediumsmall" | "medium" | "large";

type ButtonProps = {
  label?: string;
  inactive?: boolean;
  onClick?: () => void;
  className?: string;
  icon?: React.ReactNode;
  variant?: Variant;
  shape?: Shape;
  size?: Size;
  isLoading?: boolean;
  fullWidth?: boolean;
  id?: string;
};

export const Button = (props: ButtonProps) => {
  const [isPressed, setIsPressed] = useState(false);
  const { variant = "black", shape = "rectangle", size = "medium", inactive, isLoading, fullWidth = true } = props;

  const handleMouseDown = () => {
    if (!inactive && !isLoading) setIsPressed(true);
  };

  const handleMouseUp = () => {
    if (!inactive && !isLoading) setIsPressed(false);
  };

  const handleOnClick = async () => {
    if (Capacitor.getPlatform() !== "web" && Capacitor.isPluginAvailable("Haptics")) {
      await Haptics.impact({ style: ImpactStyle.Light });
    }
    props.onClick && props.onClick();
  };

  const getVariantClasses = () => {
    switch (variant) {
      case "white":
        return isPressed ? "bg-gray-100 text-black" : "bg-white text-black";
      case "gray":
        return isPressed ? "bg-gray-400 text-black" : "bg-gray-300 text-black";
      case "outlined":
        return isPressed
          ? "bg-osHardBlack/10 text-osHardBlack border-2 border-osGrey-4"
          : "bg-transparent text-osHardBlack border-2 border-osGrey-4";
      case "danger":
        return isPressed ? "bg-osDanger-2 text-osDanger-1" : "bg-osDanger-2 text-osDanger-1";
      default:
        return isPressed ? "bg-osBlack text-white" : "bg-osHardBlack text-white";
    }
  };

  const getInactiveVariantClasses = () => {
    switch (variant) {
      case "white":
        return "cursor-not-allowed bg-gray-200 text-gray-400";
      case "gray":
        return "cursor-not-allowed bg-gray-200 text-gray-400";
      case "outlined":
        return "cursor-not-allowed bg-transparent text-osGrey-3 border-2 border-osGrey-3";
      case "danger":
        return "cursor-not-allowed bg-osDanger-3 text-osDanger-4";
      default:
        return "cursor-not-allowed bg-osGrey-2 text-osGrey-4";
    }
  };

  const getShapeClasses = () => {
    const sizeClasses = {
      extrasmall: shape === "circle" ? "w-[36px] h-[36px]" : fullWidth ? "w-full min-w-[36px] h-[36px]" : "h-[36px]",
      small: shape === "circle" ? "w-[40px] h-[40px]" : fullWidth ? "w-full min-w-[40px] h-[40px]" : "h-[40px]",
      mediumsmall: shape === "circle" ? "w-[52px] h-[52px]" : fullWidth ? "w-full min-w-[52px] h-[52px]" : "h-[52px]",
      medium: shape === "circle" ? "w-[60px] h-[60px]" : fullWidth ? "w-full min-w-[60px] h-[60px]" : "h-[60px]",
      large: shape === "circle" ? "w-[80px] h-[80px]" : fullWidth ? "w-full min-w-[80px] h-[80px]" : "h-[80px]"
    };

    const paddingClasses = shape === "circle" ? "p-0" : "px-4";
    const borderRadiusClasses = shape === "circle" ? "rounded-full" : "rounded-[18px]";

    return `${sizeClasses[size]} ${paddingClasses} ${borderRadiusClasses}`;
  };

  return (
    <button
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => setIsPressed(false)}
      onClick={handleOnClick}
      disabled={inactive || isLoading}
      className={`font-semibold transition-all ${getShapeClasses()} ${inactive ? getInactiveVariantClasses() : getVariantClasses()
        } ${props.className}`}
      id={props.id}
    >
      <span className="flex items-center justify-center gap-2">
        {isLoading && (
          <SmallLoader className="mr-2" invertColors={variant === "black"} />
        )}
        {props.icon}
        {props.label && props.label}
      </span>
    </button>
  );
};

